import {
  CavernsDirectionType,
  CavernsSubmodeKeyType,
  GameModeKey,
} from 'dt-common/generated/types/enums';
import { InitNewCavernsMapResult } from 'dt-common/generated/graphql-types';
import Audio from '~/Audio';
import CavernsWorker from '~/caverns-worker/index?worker';
import { logError } from '~/logger';
import messages, { CavernsStoreMessage } from '../messages';
import { CavernsTile } from 'dt-common/generated/zod';
import { MOVEMENT_EVENTS } from '~/constants';

export default function startCavernsBattleEngine(
  {
    playerId,
    setWorker,
    setActingAIUnit,
    getBattleState,
    setBattleState,
    onDoorSpriteClick,
    getCurrentCavernsLevel,
    setCurrentCavernsLevel,
    emit,
    onMissingCavernsRunData,
  }: {
    playerId: string | null;
    setWorker: (worker: Worker) => void;
    setActingAIUnit: (unitId: string) => void;
    getBattleState: () => any; // TODO: type this
    setBattleState: (val) => void; // TODO: type this
    onDoorSpriteClick: (direction: CavernsDirectionType) => void;
    getCurrentCavernsLevel: () => number;
    setCurrentCavernsLevel: (level: number) => void;
    emit: (message: CavernsStoreMessage, data?: any) => void;
    onMissingCavernsRunData: () => void;
  },
  {
    game_submode,
    new_caverns_data,
    white_team,
  }: {
    game_submode: CavernsSubmodeKeyType;
    new_caverns_data: InitNewCavernsMapResult;
    white_team;
  }
) {
  if (!playerId) {
    throw new Error('Player ID is required');
  }

  // initialize the player's heroes & starting tiles
  const room = new_caverns_data.startingRoom;

  const allUnits = new_caverns_data.white_units.reduce((result, unit) => {
    result[unit.uid] = unit;
    return result;
  }, {});

  const allTiles = room.tiles.reduce((result: Record<string, CavernsTile>, tile: CavernsTile) => {
    result[tile.uid] = tile;
    return result;
  }, {});

  const _worker = new CavernsWorker();
  setWorker(_worker);

  _worker.onerror = logError;

  // Track timeouts for cleanup
  let _autoExploreTimeout: ReturnType<typeof setTimeout> | undefined;
  let _deathDelayedCall: gsap.core.Tween | undefined;

  const WORKER_MSG_HANDLERS = {
    caverns_level_initialized: (message_data) => {
      setCurrentCavernsLevel(message_data.caverns_level);
      emit(messages.CAVERNS_BATTLE_INITIALIZED);
    },
    abilityExecuted: onAbilityExecuted,
    aiTurn: onAITurn,
    battleEngineError: onBattleEngineError,
    battleEvent: onBattleEvent,
    gotUnitState: onUnitState,
    statCostsExacted: onStatCostsExacted,
    unitCondition: onUnitCondition,
    unitConditionExpired: onUnitConditionExpired,
    room_is_clear: onRoomIsClear,
    party_defeated: () => {
      emit(messages.PARTY_DEFEATED);
    },
    missing_caverns_run_data: () => {
      onMissingCavernsRunData();
    },
    cleaned_up: () => {
      clearTimeout(_autoExploreTimeout);
      _deathDelayedCall?.kill();
      _worker?.terminate();
    },
  };

  _worker.onmessage = (event) => {
    const { message_name, message_data, battle_state } = event.data;
    if (battle_state && message_name !== 'statCostsExacted') {
      setBattleState({
        ...(getBattleState() || {}),
        ...battle_state,
      });
    }

    const handler = WORKER_MSG_HANDLERS[message_name];
    handler && handler(message_data, message_name);
  };

  _worker.postMessage(
    JSON.parse(
      JSON.stringify({
        // avoid cloning errors
        message_name: 'start_battle_engine',
        message_data: {
          battle_engine_constructor_arg: {
            battleState: {
              allTiles,
              allUnits,
              black_team: [],
              doors: room.doors,
              walls: room.walls,
              white_team,
              portal: room.portal,
            },
            battle_id: `caverns-${new_caverns_data.startingLevel}-${playerId}`,
            blackPlayerId: null,
            game_mode: GameModeKey.GAME_MODE_caverns,
            game_submode,
            whitePlayerId: playerId,
          },
          new_caverns_data,
        },
      })
    )
  );


  function onAbilityExecuted(data) {
    emit(messages.ABILITY_EXECUTED, data);
  }

  function onAITurn(data) {
    setActingAIUnit(data.aiUnitId);
    emit(messages.AI_TURN, data);
  }

  function onBattleEngineError(data) {
    logError(data, {
      module: 'CavernsStore',
      func: 'onBattleEngineError',
    });
  }

  function onBattleEvent(data) {
    emit(messages.BATTLE_EVENT, data);

    // Death handling must run regardless of tab visibility to keep battleState synchronized
    if (data.event.eventHandle === 'death') {
      // remove the dead unit from the battleState
      const battleState = getBattleState();
      // after a delay to allow death rendering & battle log to work
      _deathDelayedCall?.kill();
      _deathDelayedCall = TweenMax.delayedCall(0.1, () => {
        if (!battleState || !battleState.allPieces) {
          return;
        }

        const victimId = data.event.victimId;
        delete battleState.allPieces[victimId];
        delete battleState.allUnits[victimId];
      });
    }
  }

  function onStatCostsExacted(data) {
    if (!document.hidden) {
      emit(messages.STAT_COSTS_EXACTED, data);
    }
  }

  function onUnitCondition(condition) {
    emit(messages.UNIT_CONDITION, condition);
  }

  function onUnitConditionExpired(data) {
    if (!document.hidden) {
      emit(messages.UNIT_CONDITION_EXPIRED, data);
    }
  }

  function onUnitState(unit) {
    if (!document.hidden) {
      emit(messages.GOT_UNIT_STATE, unit);
    }
  }

  function onRoomIsClear() {
    emit(messages.ROOM_IS_CLEAR);
    const current_caverns_level = getCurrentCavernsLevel();
    clearTimeout(_autoExploreTimeout);
    _autoExploreTimeout = setTimeout(() => {
      Audio.play('level_up');

      // check for auto-explore
      if (localStorage.getItem('dt_caverns_auto_explore') === 'true') {
        const target_depth = parseInt(
          localStorage.getItem(`dt_caverns_target_depth.${game_submode}`) ||
          current_caverns_level,
          10
        );
        let exits = getBattleState().doors.map((d) => d.direction);
        if (target_depth > current_caverns_level && exits.includes('DOWN')) {
          onDoorSpriteClick({
            direction: 'DOWN',
            game_submode,
          });
        } else if (target_depth < current_caverns_level && exits.includes('UP')) {
          onDoorSpriteClick({
            direction: 'UP',
            game_submode,
          });
        } else {
          exits = exits.filter((d) => d !== 'DOWN' && d !== 'UP');
          onDoorSpriteClick({
            direction: exits[Math.floor(Math.random() * exits.length)],
            game_submode,
          });
        }
      }
    }, 500);
  }
}
